import { fabric } from 'fabric';

/**
 * CheckBackEngine - v41 Per-Design Backs
 *
 * Changes (2026-11-03, from the check-back research spec):
 * - The per-design config the callers already computed is now actually
 *   consumed: `tint` (field fill = pale mix of the design primary),
 *   `panelLines` (security panel wording sourced ONLY from the design's
 *   real features / template specs), `watermarkText`, variant extras
 *   (ImageMatch line, Treasury USA microprint endorsement line, eCheck
 *   instructions box, money-order / traveler's strip notes), frame
 *   treatments and the Canadian neutral footnote.
 * - The business deposit block prints the account-holder/business name
 *   above FOR DEPOSIT ONLY (the canonical stamp order).
 * - All new config fields are OPTIONAL: a caller that passes none of
 *   them (e.g. UltraRealisticCheckBuilder) gets exactly the previous
 *   behaviour — default field, canonical six panel lines, Reg CC
 *   footnote, ORIGINAL/DOCUMENT watermark.
 * - `signatureImage` and the unused depositConfig fields
 *   (name/date/signatureFont/handwritingColor) are accepted for
 *   backward compatibility and DELIBERATELY ignored: a check back must
 *   never carry a pre-printed payee signature, and nothing here may
 *   start rendering one.
 *
 * The endorsement-zone and security-box GEOMETRY is owner-locked
 * (see the comments in addUnlockedEndorsementZone /
 * addUnlockedSecurityBox). Do not re-space it.
 */
export class CheckBackEngine {
    static generateCheckBack(width: number, height: number, config: any, scaleFactor: number, depositConfig?: any, signatureImage?: string | null): fabric.Object[] {
        // Deliberately unused — see the header comment. Backs never
        // render a signature.
        void signatureImage;
        const objects: fabric.Object[] = [];
        const scale = scaleFactor / 72;
        const cfg = config || {};
        const style = cfg.style || 'mobile'; // Default to mobile if not specified

        // 0. Base Draggable Backgrounds (User can adjust split manually)
        const endorsementX = width - (1.5 * 72 * scale);

        // Left Panel (Main Security Body) — the design's own field: a
        // pale mix of its primary colour (a *screen*, kept light enough
        // for bank stamps to stay legible), or the variant's explicit
        // field fill. No tint -> the previous default for every caller
        // that does not supply one.
        const fieldFill = cfg.fieldFill || (cfg.tint ? paleMix(cfg.tint) : '#e3f2fd');
        objects.push(new fabric.Rect({
            id: 'back-bg-left', // UNIQUE ID
            left: 0,
            top: 0,
            width: endorsementX,
            height: height,
            fill: fieldFill,
            selectable: true, evented: true, hasControls: true, lockRotation: true
        } as any));

        // Right Panel (Endorsement Zone - the warm ivory of the reference
        // back; Treasury gets a pale green ivory, the printed eCheck
        // plain white — both arrive as cfg.stripFill)
        objects.push(new fabric.Rect({
            id: 'back-bg-right', // UNIQUE ID
            left: endorsementX,
            top: 0,
            width: width - endorsementX,
            height: height,
            fill: cfg.stripFill || '#faf6ec',
            selectable: true, evented: true, hasControls: true, lockRotation: true
        } as any));

        // 1. Watermark (Horizontal Center - Draggable, Big & Bold).
        //    watermarkText === null means the variant prints none (V7/V7L).
        if (cfg.watermarkText !== null) {
            this.addHorizontalWatermark(width, height, scale, objects, cfg.watermarkText || ['ORIGINAL', 'DOCUMENT']);
        }

        // 1b. ImageMatch line (V3 bank-issued): the front's routing and
        //     check numbers repeated in light ink in the open FI field,
        //     so front/back images can be matched. Open field only —
        //     never in the strip, never in the panel.
        if (cfg.imageMatch && cfg.imageMatch.routingNumber) {
            const openL = 202 * scale;
            objects.push(new fabric.Text(`${cfg.imageMatch.routingNumber}  ${cfg.imageMatch.checkNumber || ''}`.trim(), {
                id: 'back-imagematch', // UNIQUE ID
                left: openL + (36 * scale),
                top: height * 0.5,
                fontSize: 9 * scale,
                fontFamily: 'Courier New, monospace',
                fill: 'rgba(0, 0, 0, 0.28)',
                angle: 90,
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
        }

        // 2. Endorsement Zone (Right Side - All elements unlocked)
        this.addUnlockedEndorsementZone(width, height, scale, objects, style, depositConfig, cfg);

        // 3. Security Box (Left Side - All elements unlocked), or the
        //    variant that replaces it. panelMode 'none' prints NO
        //    security panel (a printed eCheck on plain paper has no
        //    security features to claim); V7 stands an instructions
        //    box in the panel's place instead.
        if (cfg.panelMode === 'none') {
            if (cfg.instructions) {
                this.addEcheckInstructionsBox(width, height, scale, objects);
            }
        } else {
            this.addUnlockedSecurityBox(width, height, scale, objects, {
                lines: cfg.panelLines,
                extended: !!cfg.panelExtended,
                footnote: cfg.footnote || 'regcc',
                frameStroke: cfg.frameStroke || null,
                frameInner: cfg.frameInner || null
            });
        }

        return objects;
    }

    private static addHorizontalWatermark(width: number, height: number, scale: number, objects: fabric.Object[], lines: [string, string]): void {
        const endorsementX = width - (1.5 * 72 * scale);
        // The open area between the security box (ends at 202*scale, see
        // addUnlockedSecurityBox) and the endorsement strip. The watermark
        // is centred and sized on THIS area so it never runs under the box
        // or into the strip.
        const openL = 202 * scale;
        const openW = endorsementX - openL;
        // The fit divisor scales with the longest word (the original
        // 7.74 was measured for the 8-character DOCUMENT).
        const longest = Math.max(lines[0].length, lines[1].length, 1);
        const commonProps = {
            left: openL + (openW / 2),
            fontSize: Math.min(96 * scale, ((openW - (24 * scale)) / (longest * 0.9675))),
            fontFamily: 'Arial Black, Arial',
            fontWeight: 900,
            fill: 'rgba(180, 180, 180, 0.18)',
            originX: 'center',
            originY: 'center',
            selectable: true, evented: true, hasControls: true,
            charSpacing: 16 * scale
        };

        objects.push(new fabric.Text(lines[0], {
            ...commonProps,
            id: 'watermark-original', // UNIQUE ID
            top: (height * 0.38)
        } as any));

        objects.push(new fabric.Text(lines[1], {
            ...commonProps,
            id: 'watermark-document', // UNIQUE ID
            top: (height * 0.62)
        } as any));
    }

    private static addUnlockedEndorsementZone(width: number, height: number, scale: number, objects: fabric.Object[], style: string, config?: any, backCfg?: any): void {
        const zoneWidth = 1.5 * 72 * scale;
        const zoneX = width - zoneWidth;

        // OWNER'S CALCULATED GEOMETRY - restored exactly as originally
        // designed. The three endorsement lines sit at zoneX + 30% of the
        // zone, then +35 steps, running from y=60 to 55% of the height;
        // the X, header, mobile column and notice columns interlock with
        // those positions. Do not re-space: on a check, small placement
        // errors are what get documents rejected.
        // The notice texts keep their original columns; what changed in
        // the 2026-11-03 tidy is only their vertical organization (see
        // the lower band below): the staggered near-bottom anchors left
        // the strip's lower half looking scattered, so the notices and
        // the mobile/deposit group now share one aligned centre line.

        // --- Divider Line ---
        objects.push(new fabric.Line([zoneX, 0, zoneX, height], {
            id: 'endorse-divider', // UNIQUE ID
            stroke: '#999', strokeWidth: 1.2 * scale,
            selectable: true, evented: true, hasControls: true
        } as any));

        // --- "ENDORSE HERE" Header ---
        objects.push(new fabric.Text("ENDORSE HERE", {
            id: 'endorse-header', // UNIQUE ID
            left: width - (15 * scale),
            top: 40 * scale,
            fontSize: 12 * scale,
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fill: '#333',
            angle: 90,
            selectable: true, evented: true, hasControls: true
        } as any));

        // --- Signature X ---
        // On the reference back the X stands at the head of the first
        // endorsement line, on the line's own column - not beside it.
        const xPosX = zoneX + (zoneWidth * 0.3);
        objects.push(new fabric.Text("X", {
            id: 'endorse-x', // UNIQUE ID
            left: xPosX,
            top: 60 * scale,
            fontSize: 16 * scale,
            fontFamily: 'Arial',
            fill: '#000',
            angle: 90,
            originX: 'center',
            selectable: true, evented: true, hasControls: true
        } as any));

        // --- 3 Endorsement Lines (the calculated spacing) ---
        const lineStartTop = 60 * scale;
        const lineEndTop = height * 0.55;

        for (let i = 0; i < 3; i++) {
            const lx = xPosX + (i * 35 * scale);
            if (i === 0 && backCfg?.treasuryMicroprint) {
                // U.S. Treasury treatment: the first endorsement line is
                // microprinted "USAUSAUSA…" — SAME coordinates, SAME id,
                // only the rendering differs (a repeating microtext runs
                // along the line instead of a stroke).
                objects.push(new fabric.Text('USA'.repeat(16), {
                    id: 'endorse-line-0', // UNIQUE ID — unchanged
                    left: lx,
                    top: lineStartTop,
                    fontSize: 4.5 * scale,
                    fontFamily: 'Arial',
                    fill: '#555',
                    angle: 90,
                    originX: 'left',
                    originY: 'center',
                    selectable: true, evented: true, hasControls: true
                } as any));
                continue;
            }
            objects.push(new fabric.Line([lx, lineStartTop, lx, lineEndTop], {
                id: `endorse-line-${i}`, // UNIQUE ID
                stroke: '#ccc', strokeWidth: 1.5 * scale,
                selectable: true, evented: true, hasControls: true
            } as any));
        }

        // --- Lower-section band (2026-11-03 tidy) ---
        // Everything that lives below the endorsement lines is arranged
        // as ONE aligned band: the mobile/deposit group, the warning and
        // the reserved notice each keep their own original column, but
        // all share a single centre line, so the strip's lower half
        // reads as ordered blocks instead of staggered texts crowding
        // the bottom corner. The band starts below the lines' zone
        // (the lines end at 55% of the height) and keeps a clear margin
        // from the strip's bottom edge.
        const lowerBandTop = height * 0.59;
        const lowerBandBottom = height - (14 * scale);
        const lowerCenterY = (lowerBandTop + lowerBandBottom) / 2;

        // --- Mobile Deposit Area ---
        // The checkbox group sits in the lower band, in its original
        // columns (instruction 0.75, checkbox 0.85).
        if (style === 'mobile') {
            const mobileY = lowerCenterY;

            objects.push(new fabric.Text("CHECK HERE IF MOBILE DEPOSIT", {
                id: 'mobile-instruction', // UNIQUE ID
                left: zoneX + (zoneWidth * 0.75),
                top: mobileY,
                fontSize: 7 * scale,
                fontWeight: 'bold',
                fontFamily: 'Arial',
                fill: '#222',
                angle: 90,
                originX: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));

            objects.push(new fabric.Rect({
                id: 'mobile-checkbox', // UNIQUE ID
                left: zoneX + (zoneWidth * 0.85),
                top: mobileY,
                width: 16 * scale,
                height: 16 * scale,
                fill: 'transparent',
                stroke: '#222',
                strokeWidth: 1.5 * scale,
                angle: 90,
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
        }

        // --- Deposit-Only Styles (Business / ATM) ---
        // Same endorsement columns as the mobile block above, and the
        // same lower-band centre line: the deposit style changes the
        // restrictive endorsement wording, never the owner's layout.
        // The business block follows the canonical endorsement-stamp
        // order — account-holder/business NAME, then FOR DEPOSIT ONLY,
        // then the account number — with the name stacked above the
        // restriction in the same 0.75 column when a name is configured.
        if (style === 'business' || style === 'atm') {
            const businessName = style === 'business' ? (config?.businessName ? String(config.businessName) : '') : '';
            if (businessName) {
                objects.push(new fabric.Text(businessName.toUpperCase(), {
                    id: 'deposit-only-name',
                    left: zoneX + (zoneWidth * 0.75),
                    top: lowerCenterY - (34 * scale),
                    fontSize: 6 * scale,
                    fontWeight: 'bold',
                    fontFamily: 'Arial',
                    fill: '#222',
                    angle: 90,
                    originX: 'center',
                    selectable: true, evented: true, hasControls: true
                } as any));
            }
            const depositY = businessName ? lowerCenterY + (24 * scale) : lowerCenterY;
            objects.push(new fabric.Text("FOR DEPOSIT ONLY", {
                id: 'deposit-only-label',
                left: zoneX + (zoneWidth * 0.75),
                top: depositY,
                fontSize: 7 * scale,
                fontWeight: 'bold',
                fontFamily: 'Arial',
                fill: '#222',
                angle: 90,
                originX: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
            if (style === 'business' && config?.accountNumber) {
                objects.push(new fabric.Text(String(config.accountNumber), {
                    id: 'deposit-only-account',
                    left: zoneX + (zoneWidth * 0.85),
                    top: depositY,
                    fontSize: 7 * scale,
                    fontFamily: 'Arial',
                    fill: '#222',
                    angle: 90,
                    originX: 'center',
                    selectable: true, evented: true, hasControls: true
                } as any));
            }
        }

        // --- Warning Text (original column; centred on the lower band) ---
        // At 6 units the warning is ~155 long - longer than the lower
        // band itself - which is why it used to start up beside the
        // endorsement lines and crowd the strip's bottom corner. At 5
        // units (~125 long) it fits the band on the shared centre line
        // with margin at both ends, in its original column. Darkened to
        // #333 (spec C6) so it reads as the zone's boundary legend —
        // emphasis only, its column and centre line do not move.
        objects.push(new fabric.Text("DO NOT WRITE, SIGN OR STAMP BELOW THIS LINE", {
            id: 'endorse-warning', // UNIQUE ID
            left: zoneX + (zoneWidth * 0.5),
            top: lowerCenterY,
            fontSize: 5 * scale,
            fontWeight: '800',
            fontFamily: 'Arial',
            fill: '#333',
            angle: 90,
            originX: 'center',
            originY: 'center',
            selectable: true, evented: true, hasControls: true
        } as any));

        // --- Reserved Text (original column; centred on the lower band) ---
        // A variant may replace this column with its own strip note
        // (money order: PAYEE ENDORSEMENT ONLY; traveler's check:
        // COUNTERSIGNATURE ON FRONT) — same column, same centre line,
        // never both texts crowding one column.
        if (backCfg?.stripNote) {
            objects.push(new fabric.Text(String(backCfg.stripNote), {
                id: 'strip-note', // UNIQUE ID
                left: zoneX + (zoneWidth * 0.2),
                top: lowerCenterY,
                fontSize: 5.5 * scale,
                fontWeight: 'bold',
                fontFamily: 'Arial',
                fill: '#555',
                angle: 90,
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
        } else {
            objects.push(new fabric.Text("RESERVED FOR FINANCIAL INSTITUTION USE*", {
                id: 'endorse-reserved', // UNIQUE ID
                left: zoneX + (zoneWidth * 0.2),
                top: lowerCenterY,
                fontSize: 5 * scale,
                fontFamily: 'Arial',
                fill: '#888',
                angle: 90,
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
        }
    }

    private static addEcheckInstructionsBox(width: number, height: number, scale: number, objects: fabric.Object[]): void {
        // V7 Printed eCheck: the recipient prints the front on plain
        // paper — there IS no security stock and no feature list to
        // print. An instructions box stands in the security panel's
        // place (same slab geometry) and says exactly that, mirroring
        // the issuer guidance for printed eChecks.
        const boxLeft = 2 * scale;
        const boxTop = 24 * scale;
        const boxW = 200 * scale;
        const boxH = height - (50 * scale);
        const colY = boxTop + (boxH / 2);
        const col = (offset: number) => boxLeft + (offset * scale);

        objects.push(new fabric.Rect({
            id: 'echeck-instructions-frame', // UNIQUE ID
            left: boxLeft, top: boxTop, width: boxW, height: boxH,
            fill: 'transparent', stroke: '#888', strokeWidth: 1.2 * scale,
            selectable: true, evented: true, hasControls: true
        } as any));

        const colText = (id: string, text: string, offset: number, fontSize: number, fontWeight: string) => {
            objects.push(new fabric.Text(text, {
                id,
                left: col(offset),
                top: colY,
                fontSize: fontSize * scale,
                fontWeight,
                fontFamily: 'Arial',
                fill: '#333',
                angle: 90,
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
        };

        colText('echeck-instructions', 'PRINTED ELECTRONIC CHECK', 40, 8, 'bold');
        colText('echeck-instructions-2', 'ENDORSE IN THE AREA ABOVE', 95, 7, 'normal');
        colText('echeck-instructions-3', 'NO SECURITY PAPER FEATURES APPLY', 150, 7, 'normal');
    }

    private static addUnlockedSecurityBox(width: number, height: number, scale: number, objects: fabric.Object[], opts?: { lines?: string[]; extended?: boolean; footnote?: string; frameStroke?: string | null; frameInner?: string | null }): void {
        // The security box arranged exactly as the owner's reference back
        // (his instruction, 2026-10-26: "like how it is on the last image
        // I sent you"). The box hugs the left edge and runs nearly the
        // full height of the check. Inside, every line is rotated the
        // same way: a small Reg. CC footnote strip along the box's left
        // edge, the security features as single bulleted lines on an
        // even pitch starting on one shared line, and the SECURITY FEATURES title as a bold column
        // at the box's right edge. Nothing else lives in the box.
        // First placement (left 8, height H-70) still read as a floating
        // inset box against the photo - in the photo the frame nearly
        // touches the check's left edge and the box is a tall slab with
        // only ~7% margins top and bottom. These numbers match that.
        const boxLeft = 2 * scale;
        const boxTop = 24 * scale;
        const boxW = 200 * scale;
        const boxH = height - (50 * scale);
        const colY = boxTop + (boxH / 2);
        const col = (offset: number) => boxLeft + (offset * scale);

        // Frame — the stroke follows the design family (V8/V9 navy,
        // V10 the design primary, canonical grey otherwise), and a
        // variant may add a thin inner rule inside the same frame
        // (BofA two-tone, V3 double rule). The frame geometry itself
        // never changes.
        objects.push(new fabric.Rect({
            id: 'security-box-frame', // UNIQUE ID
            left: boxLeft, top: boxTop, width: boxW, height: boxH,
            fill: 'transparent', stroke: opts?.frameStroke || '#888', strokeWidth: 1.2 * scale,
            selectable: true, evented: true, hasControls: true
        } as any));
        if (opts?.frameInner) {
            objects.push(new fabric.Rect({
                id: 'security-box-inner-frame', // UNIQUE ID
                left: boxLeft + (4 * scale), top: boxTop + (4 * scale),
                width: boxW - (8 * scale), height: boxH - (8 * scale),
                fill: 'transparent', stroke: opts.frameInner, strokeWidth: 0.8 * scale,
                selectable: true, evented: true, hasControls: true
            } as any));
        }

        const colText = (id: string, text: string, offset: number, fontSize: number, fontWeight: string, fill: string, charSpacing?: number) => {
            objects.push(new fabric.Text(text, {
                id,
                left: col(offset),
                top: colY,
                fontSize: fontSize * scale,
                fontWeight,
                fontFamily: 'Arial',
                fill,
                angle: 90,
                originX: 'center',
                originY: 'center',
                ...(charSpacing ? { charSpacing: charSpacing * scale } : {}),
                selectable: true, evented: true, hasControls: true
            } as any));
        };

        // Footnote strip (the small print along the box's left edge).
        // US backs carry the Reg CC line; the Canadian variant swaps in
        // a neutral line (CPA practice — no Reg CC claim), and a
        // footnote of 'none' prints nothing.
        const footnote = opts?.footnote ?? 'regcc';
        if (footnote === 'regcc') {
            colText('security-regcc',
                "FEDERAL RESERVE REG CC COMPLIANT",
                10, 4.5, 'normal', '#333');
        } else if (footnote === 'neutral') {
            colText('security-regcc',
                "SECURITY FEATURES — SEE FRONT",
                10, 4.5, 'normal', '#333');
        }

        // The features, one line each, on an even pitch, all STARTING
        // on one shared line just inside the box's top edge (the
        // 2026-11-03 tidy — a list reads as one ordered set when its
        // entries share a start line; for these rotated texts the
        // start sits at the object's local left edge, so originX
        // 'left' with one shared top aligns every bullet).
        // The LINES THEMSELVES now come from the caller (config
        // panelLines — sourced only from the design's real features or
        // its template's own specs). No lines supplied -> the
        // canonical six, which is the backward-compatible behaviour
        // for callers that predate per-design backs. An explicitly
        // EMPTY list prints an empty panel: a design must not claim
        // features its front does not have.
        // Pitch is locked at 25 for up to 6 lines; the V3 extended
        // panel (up to 8 lines) reduces the pitch INSIDE the same box.
        const features = opts?.lines ?? [
            "• FLUORESCENT FIBER TECHNOLOGY",
            "• CHEMICAL SENSITIVE PAPER STOCK",
            "• MICROPRINTING ALONG LINES (MP)",
            "• SECURITY SCREEN BACKDROP",
            "• ROUTING NUMBER VERIFICATION",
            "• AUTHENTIC DOCUMENT WATERMARK"
        ];
        const pitch = features.length > 6
            ? Math.max(16, Math.floor((174 - 26 - 14) / (features.length - 1)))
            : 25;
        const featureStartY = boxTop + (18 * scale);
        features.forEach((line, i) => {
            objects.push(new fabric.Text(line, {
                id: `security-feature-${i}`,
                left: col(26 + (i * pitch)),
                top: featureStartY,
                fontSize: 7.0 * scale,
                fontWeight: '600',
                fontFamily: 'Arial',
                fill: '#111',
                angle: 90,
                originX: 'left',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
        });

        // Title column at the box's right edge
        colText('security-header',
            "SECURITY FEATURES",
            174, 10, 'bold', '#111', 2);
    }

}

/**
 * Mix a #rrggbb colour toward white — the printer's "screen" version of
 * a design's ink. ratio 0.92 ≈ the spec's ~92% pale mix (§5.2): light
 * enough that bank stamps over it stay legible. Returns null for
 * anything that is not a 6-digit hex colour.
 */
export function paleMix(hex: string, ratio = 0.92): string | null {
    const m = /^#?([0-9a-fA-F]{6})$/.exec(String(hex || '').trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    const mix = (c: number) => Math.round(c + (255 - c) * ratio);
    const r = mix((n >> 16) & 255), g = mix((n >> 8) & 255), b = mix(n & 255);
    return `rgb(${r}, ${g}, ${b})`;
}
